import { useEffect, useMemo, useState } from 'react';
import {
  PageHeader,
  LoadingSkeleton,
  ErrorState,
  StaggerReveal,
  toast,
  activityRail,
} from '../../components/index.js';
import { useApi } from '../../lib/useApi.js';
import { api } from '../../lib/apiClient.js';
import { applyDisplayPreferences } from './displayPreferences.js';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { NavMenuEditor } from './NavMenuEditor.jsx';
import './me.css';
import './me.redesign.css';

// Only preferences the product can actually honor are rendered (rebuild
// contract D11: "Settings that can't be honored? Don't render them"). The
// former notification toggles — "Email summary every morning", "In-app digest
// for critical alerts", and "Weekly executive rollup" — were placebo controls
// with no delivery pipeline or consumer behind them, so they were removed
// rather than persisted. Both remaining toggles are honored by
// applyDisplayPreferences() + the `:root[data-density]` / `:root[data-motion]`
// CSS in styles/app.css.
// The live shell owns the rich-tooltips state; Settings shares its seam.
// `tooltipsDisabled` is the canonical (negative) boolean persisted to both the
// profile blob and the legacy localStorage key AppShell reads; the event lets
// the running shell react without a reload (PRIME-SHELL-UX-002).
const TOOLTIPS_LEGACY_STORAGE_KEY = 'dha_prime__tooltips_disabled';
const TOOLTIPS_PREFERENCE_EVENT = 'prime:tooltips-preference-changed';

const PREFERENCES = [
  {
    section: 'Display',
    key: 'compactDensity',
    label: 'Compact density',
    desc: 'Tighten table and card spacing for dense, data-heavy reviews.',
    onChip: 'Compact UI',
    default: false,
  },
  {
    section: 'Display',
    key: 'reduceMotion',
    label: 'Reduce non-essential motion',
    desc: 'Suppress entrance animation beyond what your OS already requests.',
    onChip: 'Calm motion',
    default: false,
  },
  {
    // PRIME-SHELL-UX-002 (AC-02): migrated out of the top-bar Workspace menu.
    // Shown as a positive switch ("Rich tooltips" ON) but stored as the
    // canonical negative `tooltipsDisabled` so the saved payload and the shell
    // seam speak one vocabulary. `invert` flips only the displayed/active state.
    section: 'Display',
    key: 'tooltipsDisabled',
    label: 'Rich tooltips',
    desc: 'Show hover and click explainer cards across charts, tables, and admin screens.',
    onChip: 'Rich tooltips',
    default: false,
    invert: true,
    legacyStorageKey: TOOLTIPS_LEGACY_STORAGE_KEY,
  },
];

// Some preferences are stored in a negative sense (tooltipsDisabled) but shown
// as a positive, user-facing switch. `invert` flips only the DISPLAYED/active
// state; the stored boolean keeps its canonical meaning end to end.
function isPrefActive(pref, value) {
  return pref.invert ? !value : !!value;
}

const SECTION_META = {
  Display: {
    eyebrow: 'Display & comfort',
    subtitle: 'Tune the boardroom view to your screen. Applied on save.',
  },
};

function PreferenceToggle({ pref, checked, onToggle }) {
  return (
    <label className={`toggle-card ${checked ? 'is-on' : ''}`}>
      <span className="toggle-card__body">
        <span className="toggle-card__label">{pref.label}</span>
        <span className="toggle-card__desc">{pref.desc}</span>
        <span className="toggle-card__state">{checked ? 'On' : 'Off'}</span>
      </span>
      <span className="switch">
        <input
          className="switch__input"
          type="checkbox"
          checked={checked}
          onChange={() => onToggle(pref.key)}
          aria-label={pref.label}
        />
        <span className="switch__track" aria-hidden>
          <span className="switch__thumb" />
        </span>
      </span>
    </label>
  );
}

export function UserSettingsPage() {
  const defaults = useMemo(
    () => Object.fromEntries(PREFERENCES.map((p) => [p.key, p.default])),
    [],
  );
  const { user } = useCurrentUser();
  const prefsApi = useApi('/me/preferences');
  const [prefs, setPrefs] = useState(defaults);
  const [savedPrefs, setSavedPrefs] = useState(defaults);
  const [saving, setSaving] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  // Hydrate the form once from the persisted server blob, then apply the
  // stored display preferences so the toggles demonstrably reflect reality.
  useEffect(() => {
    if (hydrated || !prefsApi.data) return;
    const remote =
      prefsApi.data?.data && typeof prefsApi.data.data === 'object' ? prefsApi.data.data : {};
    const merged = { ...defaults };
    for (const p of PREFERENCES) {
      if (typeof remote[p.key] === 'boolean') {
        merged[p.key] = remote[p.key];
      } else if (p.legacyStorageKey && typeof window !== 'undefined') {
        // Honor the legacy localStorage seam for users who set the preference
        // before it was persisted to the profile (PRIME-SHELL-UX-002).
        try {
          const raw = window.localStorage.getItem(p.legacyStorageKey);
          if (raw != null) merged[p.key] = raw === 'true';
        } catch {
          /* storage unavailable — fall back to the default */
        }
      }
    }
    setPrefs(merged);
    setSavedPrefs(merged);
    applyDisplayPreferences(merged);
    setHydrated(true);
  }, [prefsApi.data, hydrated, defaults]);

  const toggle = (key) => setPrefs((p) => ({ ...p, [key]: !p[key] }));

  const dirty = useMemo(
    () => PREFERENCES.some((p) => prefs[p.key] !== savedPrefs[p.key]),
    [prefs, savedPrefs],
  );

  const activeChips = PREFERENCES.filter((p) => isPrefActive(p, prefs[p.key]));
  const sections = ['Display'];

  async function save() {
    if (saving) return;
    const payload = Object.fromEntries(PREFERENCES.map((p) => [p.key, !!prefs[p.key]]));
    setSaving(true);
    try {
      await api.patch('/me/preferences', payload);
      setSavedPrefs(payload);
      applyDisplayPreferences(payload);
      // PRIME-SHELL-UX-002: mirror the rich-tooltips value into the live shell
      // seam so RichTooltipProvider reacts immediately (localStorage is the
      // in-session source AppShell reads; the event pushes the change without a
      // reload). The profile PATCH above is what makes it cross-device durable.
      if (typeof payload.tooltipsDisabled === 'boolean') {
        try {
          window.localStorage.setItem(
            TOOLTIPS_LEGACY_STORAGE_KEY,
            String(payload.tooltipsDisabled),
          );
        } catch {
          /* storage unavailable — the profile value still persists cross-device */
        }
        window.dispatchEvent(
          new CustomEvent(TOOLTIPS_PREFERENCE_EVENT, {
            detail: { tooltipsDisabled: payload.tooltipsDisabled },
          }),
        );
      }
      const on = activeChips.map((p) => p.onChip).join(', ') || 'none';
      toast.success({ title: 'Preferences saved', body: `Active: ${on}.` });
      activityRail.push({
        action: 'settings.update',
        targetType: 'preferences',
        targetId: 'self',
        actorUserId: user?.id || 'unknown',
        summary: `${activeChips.length} preference(s) on`,
      });
    } catch (e) {
      // Preserve the edited (still-dirty) form state so the user can retry.
      toast.error({ title: 'Could not save preferences', body: e.message });
    } finally {
      setSaving(false);
    }
  }

  function reset() {
    setPrefs(savedPrefs);
  }

  const header = (
    <PageHeader
      eyebrow="Me"
      title="Settings"
      subtitle="Display preferences, persisted to your PRIME profile."
    />
  );

  if (!hydrated && prefsApi.loading) {
    return (
      <>
        {header}
        <LoadingSkeleton rows={4} />
      </>
    );
  }
  if (!hydrated && prefsApi.error) {
    return (
      <>
        {header}
        <ErrorState error={prefsApi.error} onRetry={prefsApi.refresh} />
      </>
    );
  }

  return (
    <>
      {header}

      <StaggerReveal className="settings-bento">
        <div className="span-8 card pref-group">
          {sections.map((section) => {
            const meta = SECTION_META[section];
            const groupPrefs = PREFERENCES.filter((p) => p.section === section);
            return (
              <div key={section} className="pref-group">
                <div className="pref-group__eyebrow">
                  <span className="eyebrow">{meta.eyebrow}</span>
                </div>
                <h2 className="card-title">{section}</h2>
                <p className="card-subtitle">{meta.subtitle}</p>
                <div className="toggle-card-grid cols-2">
                  {groupPrefs.map((pref) => (
                    <PreferenceToggle
                      key={pref.key}
                      pref={pref}
                      checked={isPrefActive(pref, prefs[pref.key])}
                      onToggle={toggle}
                    />
                  ))}
                </div>
              </div>
            );
          })}

          <div className="settings-save-row">
            <button
              className="btn primary tactile-press"
              onClick={save}
              disabled={!dirty || saving}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <button className="btn ghost" onClick={reset} disabled={!dirty || saving}>
              Reset
            </button>
            <span className={`settings-dirty-cue ${dirty ? 'is-dirty' : ''}`}>
              {saving ? 'Saving…' : dirty ? 'Unsaved changes' : 'All changes saved'}
            </span>
          </div>
        </div>

        <div className="span-4 card me-info-card">
          <span className="eyebrow">How this works</span>
          <p className="muted small" style={{ margin: 0 }}>
            Preferences are stored on your PRIME profile and reapplied on every sign-in, on any
            device. Compact density tightens spacing across tables and cards; reduced motion
            disables non-essential entrance animation app-wide; rich tooltips show the plain-language
            explainer cards on charts, tables, and admin screens.
          </p>
          <div style={{ alignSelf: 'flex-start', width: '100%' }}>
            <span className="eyebrow">Live preview</span>
            <div className="preview-chip-row" aria-label="Active preferences preview">
              {PREFERENCES.map((pref) => (
                <span
                  key={pref.key}
                  className={`preview-chip ${isPrefActive(pref, prefs[pref.key]) ? 'is-on' : ''}`}
                >
                  <span className="preview-dot" aria-hidden />
                  {pref.onChip}
                </span>
              ))}
            </div>
          </div>
        </div>
      </StaggerReveal>

      {/* Personal navigation editor (PRIME-NAV-UX-001). Deep-linked from the rail
          footer "Customize menu" affordance via #personal-navigation. */}
      <div className="card nav-menu-editor-card">
        <NavMenuEditor />
      </div>
    </>
  );
}
